import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  IAllUserProjects,
  IProject,
  ISelect,
  ITask,
  ITimesheet,
  IUser,
} from "../../../../models";
import { TimesheetHeaderChooseTime } from "./TimesheetHeaderChooseTime";
import { TimesheetHeaderTask } from "./TimesheetHeaderTask";
import axios from "axios";
import { getApiLink } from "../../../../functions/getApiLink";
import { getBearer } from "../../../../functions/getBearer";
import { Notifications } from "../../../../components/notifications/Notifications";
import { setTasks, setChosenTimesheet } from "../../../../storage/toolkit";
import { BlockToEdit, FixedTopEdit } from "../../Timesheet";
import { TableExportCustom } from "../../../../components/table/TableExportCustom";
import { TableSelectYearMonth } from "../../../../components/table/TableSelectYearMonth";
import { TableProjectsForUser } from "../../../../components/table/TableProjectsForUser";
import { TableCalendar } from "../../../../components/table/TableCalendar";
import { SetTasks } from "../../../../api/SetTasks";
import { SetStatistic } from "../../../../api/SetStatistic";
import { Translate } from "../../../../components/translate/Translate";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { TimesheetExcel } from "../TimesheetExcel";
import { CustomSelect } from "../../../../components/customSelect/CustomSelect";
import { errors } from "../../../../constants/Errors";
import { TimesheetDocsTemplate } from "../../../../components/docs/Timesheet";
import logo from "./../../../../assets/html/img/logo-2.png";
import getCookies from "../../../../functions/getCookie";

interface ITimesheetHeaderProps {
  itemToEdit: ITask | undefined;
  isFixedEditBlock: boolean;
  itemToDuplicate: ITask | undefined;
  setItemToDuplicate: any;
}

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

const parseTimesheetMonthYear = (dateStr?: string): { month: number; year: number } | null => {
  if (!dateStr) return null;
  const parts = dateStr.split("/");
  if (parts.length !== 3) return null;

  const dd = Number(parts[0]);
  const mm = Number(parts[1]);
  const yyRaw = parts[2];
  const yy = Number(yyRaw);
  if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yy)) return null;

  const year = yyRaw.length <= 2 ? 2000 + yy : yy;
  if (mm < 1 || mm > 12) return null;
  if (year < 2000 || year > 2100) return null;

  return { month: mm, year };
};

const buildDefaultDateData = (month: number, year: number): string => {
  const today = new Date();
  const day = today.getDate();
  return `${pad2(day)}.${pad2(month)}.${year}`;
};

export const TimesheetHeader: React.FC<ITimesheetHeaderProps> = ({
  itemToEdit,
  isFixedEditBlock,
  itemToDuplicate,
  setItemToDuplicate,
}) => {
  const [HFList] = useState([
    { label: "Home", value: "home" },
    { label: "Field", value: "field" },
  ]);

  const { t } = useTranslation();
  const { timesheetId }: any = useParams();

  const isEditTask = Boolean(itemToEdit && Object.keys(itemToEdit).length);
  const isDuplicateTask = Boolean(itemToDuplicate && Object.keys(itemToDuplicate).length);

  const timesheet: ITimesheet[] = useSelector((state: any) => state.toolkit.timesheet);
  const chosenTimesheet: ITimesheet = useSelector((state: any) => state.toolkit.chosenTimesheet);
  const userData: IUser = useSelector((state: any) => state.toolkit.user);
  const tasks: ITask[] = useSelector((state: any) => state.toolkit.tasks);
  const allUserProjects: IAllUserProjects = useSelector((state: any) => state.toolkit.allUserProjects);

  const [projectData, setProjectData] = useState<IProject | undefined>();
  const [taskData, setTaskData] = useState<string>("");
  const [dateData, setDateData] = useState<string>("");
  const [timeData, setTimeData] = useState<string>("");
  const [hoursData, setHoursData] = useState<number>(0);
  const [isCancelEdit, setIsCancelEdit] = useState(false);
  const [isLoadingToAdd, setIsLoadingToAdd] = useState(false);
  const [selectedHF, setSelectedHF] = useState<any>(undefined);
  const [projectSort, setProjectSort] = useState<IProject | undefined>();
  const [isOpenCreatBlock, setIsOpenCreatBlock] = useState(false);

  const dispatch = useDispatch();

  const setIsFixedEditBlock: any = useContext(FixedTopEdit);
  const setItemEdit: any = useContext(BlockToEdit);

  const lastAutoDateRef = useRef<string>("");

  const safeSetDate = (next: string) => {
    setDateData((prev) => {
      if (prev === next) return prev;
      return next;
    });
  };

  const resolveAuthHeader = (): string | null => {
    const fromAxiosDefault = axios.defaults.headers.common?.["Authorization"];
    if (fromAxiosDefault && String(fromAxiosDefault).trim() && !String(fromAxiosDefault).includes("undefined")) {
      return String(fromAxiosDefault);
    }

    const cesToken = getCookies("access_token_ces");
    if (cesToken) return cesToken.startsWith("Bearer ") ? cesToken : `Bearer ${cesToken}`;

    const cookieCandidates = [
      getCookies("access_token"),
      getCookies("accessToken"),
      getCookies("token"),
      getCookies("jwt"),
      getCookies("Authorization"),
    ].filter(Boolean) as string[];

    const cookieToken = cookieCandidates[0];
    if (cookieToken) return cookieToken.startsWith("Bearer ") ? cookieToken : `Bearer ${cookieToken}`;

    return null;
  };

  const [tasksList, setTasksList] = useState<ISelect[]>([]);
  const [selectedTask, setSelectedTask] = useState<ISelect>({ value: "", label: "" });

  const handleCreateTask = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!selectedHF?.value) return toast.error("Fill Work Place Field");
    if (!taskData) return toast.error("Fill Task Field");
    if (!selectedTask?.value) return toast.error("Fill Category Field");
    if (!projectData?.id) return toast.error("Fill Project Field");

    setIsLoadingToAdd(true);

    const timesheetRequest: any = {
      project_id: projectData?.id,
      task: taskData,
      date: dateData,
      time: timeData,
      hours: hoursData,
      work_place: selectedHF.value,
      category_id: selectedTask?.value,
    };

    const auth = resolveAuthHeader();
    if (!auth) {
      setIsLoadingToAdd(false);
      toast.error("Unauthorized. Please login again.");
      return;
    }

    if (isEditTask) {
      setTimeout(() => {
        getBearer("patch");
        axios
          .patch(getApiLink("/api/task/edit/?task_id=" + itemToEdit?.id), timesheetRequest)
          .then(({ data }) => {
            setIsLoadingToAdd(false);
            if (data.message === "occupied_time") return toast.error(`${t("time_was_used")}`);
            if (data?.status === false) return;
            try {
              if (chosenTimesheet?.id) {
                SetStatistic(dispatch, chosenTimesheet.id);
                SetTasks(dispatch, chosenTimesheet.id);
              }
            } catch (err) {
              console.warn("Refreshing after edit failed:", err);
            }
            handleBackFromCreate();
          })
          .catch((err) => {
            setIsLoadingToAdd(false);
            toast.error("Edit failed");
            console.error(err);
          });
      }, 400);
    } else {
      getBearer("post");
      axios
        .post(getApiLink(`/api/task/add/?timesheet_id=${chosenTimesheet?.id}`), timesheetRequest)
        .then(({ data }) => {
          setIsLoadingToAdd(false);
          if (data?.status === false) return toast.error(`${t("time_was_used")}`);

          setProjectData(undefined);
          setTaskData("");
          setTimeData("");
          setHoursData(0);
          setSelectedHF(undefined);

          if (isDuplicateTask) handleBackFromCreate();

          try {
            if (chosenTimesheet?.id) {
              SetStatistic(dispatch, chosenTimesheet.id);
              SetTasks(dispatch, chosenTimesheet.id);
            }
          } catch (err) {
            console.warn("Refreshing after create failed:", err);
          }
        })
        .catch((err) => {
          setIsLoadingToAdd(false);
          if (err?.response?.status === 422) toast.error("Fill all fields please!");
          else if (err?.response?.status === 400) toast.error(errors[err.response.data.message]);
          else {
            toast.error("Create task failed");
            console.error(err);
          }
        });
    }
  };

  const handleBackFromCreate = () => {
    setIsCancelEdit(true);
    setTimeout(() => {
      setIsFixedEditBlock(false);
      setItemEdit({});
      setItemToDuplicate({});
      setIsOpenCreatBlock(false);
      setIsCancelEdit(false);
    }, isFixedEditBlock ? 400 : 0);
  };

  const handleSwitchMonth = (month: number) => {
    if (!timesheet.length) return;
    if (timesheetId) return;

    const matchingTimesheet = timesheet.filter((item) => Number(`${item.date[3]}${item.date[4]}`) === month)[0];
    if (!matchingTimesheet?.id) return;

    // Update chosenTimesheet so "Add task" targets the correct month
    dispatch(setChosenTimesheet(matchingTimesheet));
  };

  const handleOpenToCreate = () => {
    setIsOpenCreatBlock(true);
    resetFields();
  };

  const resetFields = () => {
    setProjectData(undefined);
    setTaskData("");
  };

  // Prefill when editing
  useEffect(() => {
    if (!chosenTimesheet?.date) return;
    if (itemToEdit && !Object.keys(itemToEdit).length) return;

    if (isEditTask) setIsOpenCreatBlock(true);

    document.querySelector(".simplebar-content-wrapper")?.scrollTo({ top: 0, behavior: "smooth" });

    // Шукаємо проект по name+description (об'єднані проекти різних років)
    const project = allUserProjects?.projects_list?.find(
      (p) => p?.name === itemToEdit?.project?.name && p?.description === itemToEdit?.project?.description
    ) || allUserProjects?.projects_list?.find((p) => p?.id === itemToEdit?.project?.id);
    
    // Шукаємо категорію по id або по name якщо id не знайдено
    const projectTask = project?.task_categories?.find((c) => c.id === itemToEdit?.category?.id) 
      || project?.task_categories?.find((c) => c.name === itemToEdit?.category?.name);

    if (project) setProjectData(project);
    setTimeout(() => setSelectedTask(projectTask ? { value: projectTask.id, label: projectTask.name } : { value: "", label: "" }), 100);

    setTaskData(itemToEdit?.task ?? "");

    const parsed = parseTimesheetMonthYear(chosenTimesheet?.date);
    const fallback = parsed ? buildDefaultDateData(parsed.month, parsed.year) : "";
    safeSetDate(itemToEdit?.date?.replaceAll("/", ".") ?? fallback);

    setTimeData(itemToEdit?.time ?? "");
    setHoursData(itemToEdit?.hours ?? 0);

    setTimeout(() => {
      setSelectedHF(HFList.find((h) => h.value === itemToEdit?.work_place));
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemToEdit]);

  // Prefill when duplicating
  useEffect(() => {
    if (!chosenTimesheet?.date) return;
    if (itemToDuplicate && !Object.keys(itemToDuplicate).length) return;

    if (isDuplicateTask) setIsOpenCreatBlock(true);
    document.querySelector(".simplebar-content-wrapper")?.scrollTo({ top: 0, behavior: "smooth" });

    // Шукаємо проект по name+description (об'єднані проекти різних років)
    const project = allUserProjects?.projects_list?.find(
      (p) => p?.name === itemToDuplicate?.project?.name && p?.description === itemToDuplicate?.project?.description
    ) || allUserProjects?.projects_list?.find((p) => p?.id === itemToDuplicate?.project?.id);
    
    // Шукаємо категорію по id або по name якщо id не знайдено
    const projectTask = project?.task_categories?.find((c) => c.id === itemToDuplicate?.category?.id)
      || project?.task_categories?.find((c) => c.name === itemToDuplicate?.category?.name);

    if (project) setProjectData(project);
    setTimeout(() => setSelectedTask(projectTask ? { value: projectTask.id, label: projectTask.name } : { value: "", label: "" }), 100);

    setTaskData(itemToDuplicate?.task ?? "");

    const parsed = parseTimesheetMonthYear(chosenTimesheet?.date);
    const fallback = parsed ? buildDefaultDateData(parsed.month, parsed.year) : "";
    safeSetDate(itemToDuplicate?.date?.replaceAll("/", ".") ?? fallback);

    setTimeData(itemToDuplicate?.time ?? "");
    setHoursData(itemToDuplicate?.hours ?? 0);

    setTimeout(() => {
      setSelectedHF(HFList.find((h) => h.value === itemToDuplicate?.work_place));
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemToDuplicate]);

  // Auto default date when chosenTimesheet changes
  useEffect(() => {
    if (!chosenTimesheet?.date) return;
    if (isOpenCreatBlock || isEditTask || isDuplicateTask) return;

    const parsed = parseTimesheetMonthYear(chosenTimesheet.date);
    if (!parsed) return;

    const next = buildDefaultDateData(parsed.month, parsed.year);

    setDateData((prev) => {
      const wasAuto = !prev || prev === lastAutoDateRef.current;
      if (!wasAuto) return prev;
      if (prev === next) return prev;
      lastAutoDateRef.current = next;
      return next;
    });
  }, [chosenTimesheet?.date, isOpenCreatBlock, isEditTask, isDuplicateTask]);

  // Tasks list for selected project
  useEffect(() => {
    setTasksList([]);
    if (!projectData) return;

    const selectArray: ISelect[] = (projectData.task_categories || []).map((item) => ({
      value: item.id,
      label: item.name,
    }));

    setSelectedTask(selectArray[0] ?? { value: "", label: "" });
    if (selectArray.length) setTasksList(selectArray);
  }, [projectData]);

  // Load tasks for chosenTimesheet
  useEffect(() => {
    if (!chosenTimesheet?.id) return;
    const auth = resolveAuthHeader();
    if (!auth) {
      console.warn("Skipping SetTasks: no auth token");
      return;
    }

    try {
      if (!projectSort) SetTasks(dispatch, chosenTimesheet.id);
      else SetTasks(dispatch, chosenTimesheet.id, projectSort.id);
    } catch (err) {
      console.warn("SetTasks call failed:", err);
    }
  }, [projectSort?.id, chosenTimesheet?.id, dispatch]);

  const getImage = async () => {
    const response = await fetch(logo);
    const imageArrayBuffer = await response.arrayBuffer();
    return new Uint8Array(imageArrayBuffer);
  };

  const isApprove = chosenTimesheet?.status === "approve";

  const secondTitle: { [key: string]: string } = {
    duplicate: `${t("duplicate_task")}`,
    edit: `${t("edit_task")}`,
    add: `${t("timesheet_page.top_part.add_task")}`,
  };

  const isWindowMin = isFixedEditBlock && window.innerWidth > 1200 && window.innerWidth < 1500;

  return (
    <div
      style={{
        paddingLeft: isFixedEditBlock ? (isWindowMin ? "170px" : "51px") : "0",
      }}
      className={`section-table__header ${isFixedEditBlock && "animate-to-show"} ${isCancelEdit && "animate-to-hide"}`}
    >
      <div className="section-table__header--row is-always-row">
        <div className="section-table__header--col">
          <h1 className="section-table__title title change-title" id="main-title">
            <span>
              <Translate>timesheet_page.table.timesheet</Translate>

              {isOpenCreatBlock && " / "}
              {isOpenCreatBlock && secondTitle[isDuplicateTask ? "duplicate" : isEditTask ? "edit" : "add"]}

              {chosenTimesheet?.user?.id && chosenTimesheet?.user?.id !== userData?.id && (
                <span>
                  {" "}
                  ({chosenTimesheet?.user?.first_name} {chosenTimesheet?.user?.last_name})
                </span>
              )}

              {timesheetId && chosenTimesheet?.user?.id === userData?.id && (
                <span>
                  {" "}
                  ({chosenTimesheet?.user?.first_name} {chosenTimesheet?.user?.last_name})
                </span>
              )}
            </span>
          </h1>
        </div>

        <Notifications />
      </div>

      <div className={`section-table__header--block block-for-is-active ${isOpenCreatBlock && "is-active"}`}>
        <div className="section-table__header--block-item">
          <div>
            <div className="section-table__header--row row-2">
              <div className="section-table__header--col">
                <button
                  disabled={isApprove}
                  onClick={handleOpenToCreate}
                  type="button"
                  className="section-table__add btn add-is-active"
                  data-add-active-change-title="main-title"
                >
                  <Translate>timesheet_page.top_part.add_task</Translate>
                  <svg width="16" height="15" viewBox="0 0 16 15">
                    <use xlinkHref="#plus"></use>
                  </svg>
                </button>

                <TableProjectsForUser setProjectData={setProjectSort} projectData={projectSort} />
              </div>

              <div className="section-table__header--col">
                <TableSelectYearMonth onSwitch={handleSwitchMonth} />

                <TableExportCustom
                  excelFile={() =>
                    TimesheetExcel({
                      chosenTimesheet,
                      tasks,
                      translate: t,
                      chosenProject: projectSort,
                    })
                  }
                  docxFile={() =>
                    TimesheetDocsTemplate({
                      image: getImage(),
                      tasks,
                      chosenTimesheet,
                      chosenProject: projectSort,
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="section-table__header--block-item">
          <div>
            <form onSubmit={handleCreateTask} className="section-table__header--add-task section-table__add-task">
              <button
                onClick={handleBackFromCreate}
                className="section-table__add-task--back back-btn remove-is-active"
                data-remove-active-change-title="main-title"
                type="button"
                aria-label="Go back"
              >
                <svg width="7" height="10" viewBox="0 0 7 10">
                  <use xlinkHref="#arrow-prev"></use>
                </svg>
                <span className="visible-on-mob">Go back</span>
              </button>

              <TableCalendar dateData={dateData} setDateData={safeSetDate} />

              <TableProjectsForUser projectData={projectData} setProjectData={setProjectData} />

              <CustomSelect
                disabled={!projectData}
                headText={"Task"}
                className={"department_select"}
                list={tasksList}
                setSelectedItem={setSelectedTask}
                selectValue={selectedTask}
              />

              <TimesheetHeaderTask taskData={taskData} setTaskData={setTaskData} />

              <CustomSelect headText={"H/F"} list={HFList} setSelectedItem={setSelectedHF} selectValue={selectedHF} />

              <TimesheetHeaderChooseTime
                hoursData={hoursData}
                timeData={timeData}
                setHoursData={setHoursData}
                setTimeData={setTimeData}
              />

              <button disabled={isLoadingToAdd} className="section-table__add-task--submit btn" type="submit">
                {!isLoadingToAdd ? (isEditTask ? <Translate>edit_task</Translate> : <Translate>timesheet_page.top_part.add_task</Translate>) : <Translate>loading</Translate>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
